import { useEffect } from 'react';
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, X } from 'lucide-react';
import { fetchRewardPointsSettings, type ThemeUnlockKind } from '@/lib/api';
import styles from './HowToEarnSheet.module.css';

export interface HowToEarnSheetProps {
  themeName: string;
  /** Legacy points-trade cost. Only meaningful when `unlockKind` is
   *  null/'points' equivalent — i.e. the pre-milestone path. For
   *  milestone-kind unlocks pass 0 (the post-migration catalog sets
   *  price_points to 0 on every milestone-kind row anyway). */
  themeCost: number;
  /** Driver's current total_points — drives the legacy "X pts to go"
   *  line and the "you have N pts" balance row. Unused on milestone-
   *  kind renders. */
  currentPoints: number;
  /**
   * Post unlock-model rewrite (Commit 4 of the rewrite). Decides
   * which copy the modal renders:
   *
   *   - 'kwh_milestone'     — kWh-progress branch (no points copy,
   *                           no "Earn EVI points by" list, "View
   *                           map" CTA instead of "View rewards").
   *   - 'session_milestone' — sessions-progress branch (same shape,
   *                           "sessions" instead of "kWh").
   *   - 'default' / null    — legacy points-trade branch (unchanged
   *                           behaviour for pre-migration catalogs
   *                           or any future non-milestone unlock).
   *   - 'fleet_only' /      — never reach this modal (filtered from
   *     'admin_only'          the picker for non-super_admin
   *                           drivers).
   */
  unlockKind?: ThemeUnlockKind | null;
  /** kWh or session-count threshold for the milestone branches.
   *  Null/undefined falls through to the legacy points branch. */
  unlockThreshold?: number | null;
  /** Driver's current kWh / session totals — drives the milestone-
   *  branch "you have N / target" copy. Both default to 0 if the
   *  caller doesn't pass them (loading state). */
  currentKwh?: number;
  currentSessions?: number;
  onClose: () => void;
  onNavigateRewards: () => void;
}

/**
 * Bottom-sheet explainer for locked themes. Pulled up when the driver
 * taps a locked card. Copy values come from /settings/reward-points
 * so earning rates stay in sync with admin-configured tenant values
 * (runtime-configurable per project constraints — hardcoding would
 * violate that).
 *
 * Loading state: render the sheet chrome immediately with skeletal
 * placeholders for the earning rows. If the settings fetch fails,
 * fall back to registry defaults so the sheet is still useful —
 * never block the driver from seeing "how much it costs" and the
 * call-to-action button.
 */
export function HowToEarnSheet({
  themeName,
  themeCost,
  currentPoints,
  unlockKind = null,
  unlockThreshold = null,
  currentKwh = 0,
  currentSessions = 0,
  onClose,
  onNavigateRewards,
}: HowToEarnSheetProps): JSX.Element {
  // ESC closes. Click-outside handled by the backdrop onClick.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Milestone-mode short-circuit — when the locked theme uses a kWh
  // or session-count milestone unlock criteria, render the modal
  // with charging-progress copy instead of the points-trade
  // explainer. Skips the rewards-settings query entirely (it's not
  // relevant to the charge-to-unlock flow), drops the "Earn EVI
  // points by" list, swaps the CTA from "View rewards" to "Got it".
  //
  // Falls through to the legacy points-trade branch (the unchanged
  // body below) for unlockKind null/'default'/undefined — preserves
  // the pre-rewrite behaviour for any future non-milestone unlock
  // path that lands without explicit modal copy.
  const isMilestoneMode =
    (unlockKind === 'kwh_milestone' || unlockKind === 'session_milestone')
    && unlockThreshold != null;

  if (isMilestoneMode) {
    return (
      <MilestoneSheet
        themeName={themeName}
        unlockKind={unlockKind as 'kwh_milestone' | 'session_milestone'}
        unlockThreshold={unlockThreshold}
        currentKwh={currentKwh}
        currentSessions={currentSessions}
        onClose={onClose}
      />
    );
  }

  return (
    <PointsSheet
      themeName={themeName}
      themeCost={themeCost}
      currentPoints={currentPoints}
      onClose={onClose}
      onNavigateRewards={onNavigateRewards}
    />
  );
}

/**
 * Milestone-mode modal body. Renders for kwh_milestone +
 * session_milestone unlock kinds. No "Earn points by" list — the
 * unlock surface is charging activity, not points spending.
 *
 * "Got it" close-only CTA (rather than the points-mode "View
 * rewards" deep-link) because there's nowhere to navigate the user
 * to charge faster — closing the sheet and letting them charge
 * naturally IS the path.
 */
function MilestoneSheet({
  themeName,
  unlockKind,
  unlockThreshold,
  currentKwh,
  currentSessions,
  onClose,
}: {
  themeName: string;
  unlockKind: 'kwh_milestone' | 'session_milestone';
  unlockThreshold: number;
  currentKwh: number;
  currentSessions: number;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('themes');
  const isKwh = unlockKind === 'kwh_milestone';
  const unit = isKwh ? 'kWh' : t('unit.sessions');
  const current = isKwh ? Math.floor(currentKwh) : currentSessions;
  const gap = Math.max(0, unlockThreshold - current);

  return (
    <>
      <div className={styles.backdrop} aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-to-earn-title"
        className={styles.sheet}
      >
        <header className={styles.header}>
          <span className={styles.lockBadge} aria-hidden>
            <Lock size={14} />
          </span>
          <div className={styles.headerText}>
            <div id="how-to-earn-title" className={styles.title}>
              {t('locked.title', { name: themeName })}
            </div>
            <div className={styles.subtitle}>
              {t('locked.subtitleCharged', {
                value: unlockThreshold.toLocaleString(),
                unit,
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className={styles.closeBtn}
          >
            <X size={16} />
          </button>
        </header>

        <div className={styles.balance}>
          <div className={styles.balanceRow}>
            <span>{t('balance.youHave')}</span>
            <strong className={styles.balanceValue}>
              {current.toLocaleString()} {unit}
            </strong>
          </div>
          {gap > 0 ? (
            <div className={styles.balanceRow}>
              <span>{t('balance.stillNeeded')}</span>
              <strong className={styles.gapValue}>
                {gap.toLocaleString()} {unit}
              </strong>
            </div>
          ) : (
            <div className={styles.readyNote}>
              {t('milestone.readyNote')}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {/* No deep-link CTA in milestone mode — charging IS the
              unlock path. Close-only action keeps the surface honest
              about what the driver can do from here. */}
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={onClose}
          >
            {t('gotIt')}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Points-mode modal body. Unchanged from the pre-rewrite shape —
 * renders for legacy points-trade unlocks (unlockKind null or any
 * non-milestone kind). The post-migration catalog has no live
 * points-trade unlocks, so this branch is effectively dead code
 * today, but kept as the structural fallback for any future
 * non-milestone unlock path.
 */
function PointsSheet({
  themeName,
  themeCost,
  currentPoints,
  onClose,
  onNavigateRewards,
}: {
  themeName: string;
  themeCost: number;
  currentPoints: number;
  onClose: () => void;
  onNavigateRewards: () => void;
}): JSX.Element {
  const { t } = useTranslation('themes');
  const settingsQ = useQuery({
    queryKey: ['settings', 'reward-points'],
    queryFn: fetchRewardPointsSettings,
    staleTime: 5 * 60_000,
  });

  const gap = Math.max(0, themeCost - currentPoints);

  return (
    <>
      <div className={styles.backdrop} aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-to-earn-title"
        className={styles.sheet}
      >
        <header className={styles.header}>
          <span className={styles.lockBadge} aria-hidden>
            <Lock size={14} />
          </span>
          <div className={styles.headerText}>
            <div id="how-to-earn-title" className={styles.title}>
              {t('locked.title', { name: themeName })}
            </div>
            <div className={styles.subtitle}>
              {t('locked.subtitlePts', { value: themeCost.toLocaleString() })}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className={styles.closeBtn}
          >
            <X size={16} />
          </button>
        </header>

        <div className={styles.section}>
          <p className={styles.sectionLabel}>{t('earn.label')}</p>
          {settingsQ.isLoading ? (
            <ul className={styles.list}>
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className={styles.row}>
                  <span className={styles.rowSkeleton} aria-hidden />
                </li>
              ))}
            </ul>
          ) : (
            <EarningList
              settings={settingsQ.data ?? FALLBACK_SETTINGS}
            />
          )}
        </div>

        <div className={styles.balance}>
          <div className={styles.balanceRow}>
            <span>{t('balance.youHave')}</span>
            <strong className={styles.balanceValue}>
              {t('ptsValue', { value: currentPoints.toLocaleString() })}
            </strong>
          </div>
          {gap > 0 ? (
            <div className={styles.balanceRow}>
              <span>{t('balance.stillNeeded')}</span>
              <strong className={styles.gapValue}>
                {t('ptsValue', { value: gap.toLocaleString() })}
              </strong>
            </div>
          ) : (
            <div className={styles.readyNote}>
              {t('points.readyNote')}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={onNavigateRewards}
          >
            {t('actions.viewRewards')}
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </>
  );
}

function EarningList({
  settings,
}: {
  settings: Pick<
    import('@/lib/api').RewardPointsSettings,
    | 'sessionCompletionBonus'
    | 'pointsPerKwh'
    | 'streakWeeklyReward'
    | 'referralBonusReferrer'
  >;
}): JSX.Element {
  const { t } = useTranslation('themes');
  const items: Array<{ label: string; rate: string }> = [
    {
      label: t('earn.session'),
      rate: t('earn.rate', { value: settings.sessionCompletionBonus }),
    },
    {
      label: t('earn.kwh'),
      rate: t('earn.rate', { value: settings.pointsPerKwh }),
    },
    {
      label: t('earn.streak'),
      rate: t('earn.rate', { value: settings.streakWeeklyReward }),
    },
    {
      label: t('earn.referral'),
      rate: t('earn.rate', { value: settings.referralBonusReferrer }),
    },
  ];
  return (
    <ul className={styles.list}>
      {items.map((it) => (
        <li key={it.label} className={styles.row}>
          <span className={styles.rowLabel}>{it.label}</span>
          <span className={styles.rowRate}>{it.rate}</span>
        </li>
      ))}
    </ul>
  );
}

// Registry defaults — kept in lockstep with packages/gamification/src/
// settings.ts DEFAULTS. Used as a failsafe when /settings/reward-points
// errors out so the sheet stays rendered. If this drifts from the
// server's defaults, drivers on a failed-fetch path see stale copy —
// low-impact but worth bumping when tenant defaults change materially.
const FALLBACK_SETTINGS = {
  sessionCompletionBonus: 5,
  pointsPerKwh: 1,
  streakWeeklyReward: 100,
  referralBonusReferrer: 100,
} as const;
